import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import ReportFilters, { formatReportMoney } from './ReportFilters';
import {
  downloadPipelineCsv,
  getPipelineReport,
  PipelineReport,
  ReportQuery,
} from '../../api/reportsService';

const statusClass: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-50 text-blue-700',
  signed: 'bg-green-50 text-green-700',
  cancelled: 'bg-orange-50 text-orange-700',
};

const PipelineReportPage = () => {
  const [query, setQuery] = useState<ReportQuery>({ period: '1m' });
  const [report, setReport] = useState<PipelineReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    const data = await getPipelineReport(query);
    if (!data) setError('Could not load pipeline report.');
    setReport(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [query]);

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadPipelineCsv(query);
    } catch {
      setError('Export failed.');
    } finally {
      setExporting(false);
    }
  };

  const summary = report?.summary;

  return (
    <Layout>
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Link to="/reports" className="text-sm text-torch-primary hover:underline">← Reports</Link>
          <h1 className="mt-1 text-2xl font-semibold text-gray-900">Pipeline</h1>
          <p className="text-sm text-gray-500">
            {report?.isPersonalView ? 'Your proposals' : 'All proposals'} in the selected period.
          </p>
        </div>
        <button
          type="button"
          onClick={handleExport}
          disabled={exporting || loading}
          className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
          {exporting ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      <PageCard className="mb-6">
        <ReportFilters query={query} onChange={setQuery} />
      </PageCard>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {summary && (
        <div className="mb-6 grid gap-4 sm:grid-cols-3">
          {(['draft', 'sent', 'signed'] as const).map((key) => (
            <PageCard key={key} className="!p-4">
              <p className="text-sm font-medium capitalize text-gray-500">{key}</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900">{summary[key]?.count ?? 0}</p>
              <p className="text-sm text-gray-600">{formatReportMoney(summary[key]?.value ?? 0)}</p>
            </PageCard>
          ))}
        </div>
      )}

      <PageCard>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : !report?.rows.length ? (
          <p className="text-sm text-gray-500">No proposals match these filters.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="py-2 pr-4">ID</th>
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2 pr-4">Company</th>
                  <th className="py-2 pr-4">Sales rep</th>
                  <th className="py-2 pr-4">Publications</th>
                  <th className="py-2 pr-4">Total</th>
                  <th className="py-2">Updated</th>
                </tr>
              </thead>
              <tbody>
                {report.rows.map((row) => (
                  <tr key={row.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4">
                      <Link to={`/magazine/proposals/${row.id}`} className="text-torch-primary hover:underline">
                        #{row.id}
                      </Link>
                    </td>
                    <td className="py-3 pr-4">
                      <span className={`rounded px-2 py-0.5 text-xs capitalize ${statusClass[row.status] || ''}`}>
                        {row.status}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-gray-800">{row.company}</td>
                    <td className="py-3 pr-4 text-gray-600">{row.sales_rep}</td>
                    <td className="py-3 pr-4 text-gray-600">{row.publications}</td>
                    <td className="py-3 pr-4 font-medium text-gray-900">{formatReportMoney(row.total)}</td>
                    <td className="py-3 text-gray-500">
                      {row.updated_at ? new Date(row.updated_at).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>
    </Layout>
  );
};

export default PipelineReportPage;
